/**
 * FikoTopologicalSelection - Topolojik kenar seçim sistemi
 *
 * CAD topoloji verileri (adjacentFaceA/B, radiusGroup, edgeId) kullanarak
 * gelismis secim modlari saglar:
 *
 * 1. FACE LOOP: Bir face'e ait tum kenarlari sec
 * 2. EDGE CHAIN: Bir edge'den baslayarak bagli kenarlari zincirleme sec
 * 3. RADIUS GROUP: Ayni radius'a sahip tum kenarlari sec
 * 4. TEK KENAR: Standart tek kenar secimi (varsayilan)
 *
 * Performans: Zero-allocation inner loop, pre-allocated buffers
 */

import * as THREE from 'three';

// ============================================================
// ENUM & INTERFACE
// ============================================================

export enum TopolojikSecimModu {
    /** Standart tek kenar secimi */
    TEK_KENAR = 'tek_kenar',
    /** Bir face'e ait tum kenarlari sec (dblclick) */
    FACE_LOOP = 'face_loop',
    /** Bagli kenarlari zincirleme sec (shift+click) */
    EDGE_CHAIN = 'edge_chain',
    /** Ayni radius'a sahip tum kenarlari sec */
    RADIUS_GROUP = 'radius_group',
}

export interface TopolojikSecimSonuc {
    /** Secilen edge vertex index cifltleri: [v1Start, v1End, v2Start, v2End, ...] */
    edgeVertexPairs: number[];
    /** Secilen edge'lerin toplam sayisi */
    edgeSayisi: number;
    /** Secim modu */
    mod: TopolojikSecimModu;
    /** Face ID (face loop icin) */
    faceId?: number;
    /** Radius (radius group icin) */
    radius?: number;
    /** RadiusGroup ID */
    radiusGroupId?: number;
}

// ============================================================
// TOPOLOGY INDEX: Face -> Edge ve Edge -> Edge iliskileri
// ============================================================

interface TopologyIndex {
    /** faceId -> Bu face'e ait edge vertex index listesi (cift cift) */
    faceToEdges: Map<number, number[]>;
    /** edgeId -> adjacentFaceA, adjacentFaceB */
    edgeToFaces: Map<number, { faceA: number; faceB: number }>;
    /** radiusGroupId -> edge vertex index listesi */
    radiusGroupToEdges: Map<number, number[]>;
    /** vertex index -> edgeId (v1 index ile arama icin) */
    vertexToEdgeId: Map<number, number>;
    /** edgeId -> vertex index ciftleri */
    edgeIdToVertices: Map<number, number[]>;
    /** Toplam edge sayisi */
    totalEdges: number;
    /** Index'in olusturuldugu geometry uuid'si */
    geometryUuid: string;
}

// ============================================================
// MAIN CLASS
// ============================================================

export class FikoTopologicalSelection {
    private static _instance: FikoTopologicalSelection | null = null;

    /** Geometry UUID -> TopologyIndex cache */
    private _indexCache = new Map<string, TopologyIndex>();

    /** Mevcut secim modu */
    public secimModu: TopolojikSecimModu = TopolojikSecimModu.TEK_KENAR;

    /** Cache TTL (ms) - 5 dakika */
    private readonly CACHE_TTL = 5 * 60 * 1000;
    private _cacheTimestamps = new Map<string, number>();

    /** Max cache boyutu */
    private readonly MAX_CACHE_SIZE = 50;

    static getInstance(): FikoTopologicalSelection {
        if (!FikoTopologicalSelection._instance) {
            FikoTopologicalSelection._instance = new FikoTopologicalSelection();
        }
        return FikoTopologicalSelection._instance;
    }

    // ============================================================
    // INDEX BUILDING
    // ============================================================

    /**
     * Bir edge geometry icin topoloji index'i olustur veya cache'den al
     * @param edgeGeometry BufferGeometry (adjacentFaceA/B, radiusGroup, edgeId attribute'lari olmali)
     */
    getOrBuildIndex(edgeGeometry: THREE.BufferGeometry): TopologyIndex | null {
        if (!edgeGeometry || !edgeGeometry.uuid) return null;

        const uuid = edgeGeometry.uuid;

        // Cache kontrol
        const cached = this._indexCache.get(uuid);
        if (cached) {
            return cached;
        }

        // Gerekli attribute'lar
        const adjacentFaceAAttr = edgeGeometry.getAttribute('adjacentFaceA') as THREE.BufferAttribute | undefined;
        const adjacentFaceBAttr = edgeGeometry.getAttribute('adjacentFaceB') as THREE.BufferAttribute | undefined;
        const radiusGroupAttr = edgeGeometry.getAttribute('radiusGroup') as THREE.BufferAttribute | undefined;
        const edgeIdAttr = edgeGeometry.getAttribute('edgeId') as THREE.BufferAttribute | undefined;
        const posAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute | undefined;

        if (!posAttr || posAttr.count < 2) return null;

        // En az bir topoloji attribute'u olmali
        if (!adjacentFaceAAttr && !radiusGroupAttr && !edgeIdAttr) return null;

        const vertexCount = posAttr.count;
        const edgeCount = Math.floor(vertexCount / 2);

        const faceToEdges = new Map<number, number[]>();
        const edgeToFaces = new Map<number, { faceA: number; faceB: number }>();
        const radiusGroupToEdges = new Map<number, number[]>();
        const vertexToEdgeId = new Map<number, number>();
        const edgeIdToVertices = new Map<number, number[]>();

        for (let i = 0; i < edgeCount; i++) {
            const v1 = i * 2;
            const v2 = i * 2 + 1;

            // Edge ID
            const edgeId = edgeIdAttr ? edgeIdAttr.getX(v1) : -1;
            if (edgeId >= 0) {
                vertexToEdgeId.set(v1, edgeId);

                if (!edgeIdToVertices.has(edgeId)) {
                    edgeIdToVertices.set(edgeId, []);
                }
                edgeIdToVertices.get(edgeId)!.push(v1, v2);
            }

            // Adjacent faces
            const faceA = adjacentFaceAAttr ? adjacentFaceAAttr.getX(v1) : -1;
            const faceB = adjacentFaceBAttr ? adjacentFaceBAttr.getX(v1) : -1;

            if (edgeId >= 0 && (faceA >= 0 || faceB >= 0)) {
                edgeToFaces.set(edgeId, { faceA, faceB });
            }

            // Face -> Edges mapping
            if (faceA >= 0) {
                if (!faceToEdges.has(faceA)) {
                    faceToEdges.set(faceA, []);
                }
                faceToEdges.get(faceA)!.push(v1, v2);
            }
            if (faceB >= 0) {
                if (!faceToEdges.has(faceB)) {
                    faceToEdges.set(faceB, []);
                }
                faceToEdges.get(faceB)!.push(v1, v2);
            }

            // Radius group
            const rg = radiusGroupAttr ? radiusGroupAttr.getX(v1) : -1;
            if (rg >= 0) {
                if (!radiusGroupToEdges.has(rg)) {
                    radiusGroupToEdges.set(rg, []);
                }
                radiusGroupToEdges.get(rg)!.push(v1, v2);
            }
        }

        const index: TopologyIndex = {
            faceToEdges,
            edgeToFaces,
            radiusGroupToEdges,
            vertexToEdgeId,
            edgeIdToVertices,
            totalEdges: edgeCount,
            geometryUuid: uuid,
        };

        // Cache'e ekle
        this._evictOldCache();
        this._indexCache.set(uuid, index);
        this._cacheTimestamps.set(uuid, Date.now());

        return index;
    }

    // ============================================================
    // SELECTION METHODS
    // ============================================================

    /**
     * Face Loop Secimi: Bir face'e ait tum kenarlari bul
     * @param edgeGeometry Edge BufferGeometry
     * @param faceId STEP face ID (numeric)
     */
    selectFaceLoop(edgeGeometry: THREE.BufferGeometry, faceId: number): TopolojikSecimSonuc | null {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return null;

        const edgeVertices = index.faceToEdges.get(faceId);
        if (!edgeVertices || edgeVertices.length === 0) return null;

        return {
            edgeVertexPairs: edgeVertices,
            edgeSayisi: edgeVertices.length / 2,
            mod: TopolojikSecimModu.FACE_LOOP,
            faceId,
        };
    }

    /**
     * Bir edge secildiginde, o edge'in hangi face'lere bagli oldugunu bul
     * ve o face'lere ait diger edge'leri de sec (1 adim derinlikte)
     * @param edgeGeometry Edge BufferGeometry
     * @param hitVertexIndex Secilen edge'in vertex index'i (v1)
     */
    selectFaceLoopFromEdge(edgeGeometry: THREE.BufferGeometry, hitVertexIndex: number): TopolojikSecimSonuc | null {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return null;

        const edgeId = index.vertexToEdgeId.get(hitVertexIndex);
        if (edgeId === undefined || edgeId < 0) return null;

        const faces = index.edgeToFaces.get(edgeId);
        if (!faces) return null;

        // adjacentFaceA ve adjacentFaceB'nin ikisinden de edge'leri topla
        const collectedVertices: number[] = [];
        const seenEdges = new Set<number>();

        for (const fId of [faces.faceA, faces.faceB]) {
            if (fId < 0) continue;
            const faceEdges = index.faceToEdges.get(fId);
            if (!faceEdges) continue;

            // Her edge cifti icin (v1, v2) tekrar kontrolu
            for (let i = 0; i < faceEdges.length; i += 2) {
                const eId = index.vertexToEdgeId.get(faceEdges[i]);
                if (eId !== undefined && !seenEdges.has(eId)) {
                    seenEdges.add(eId);
                    // Bu edge ID'ye ait tum vertex'leri ekle
                    const vertices = index.edgeIdToVertices.get(eId);
                    if (vertices) {
                        for (let j = 0; j < vertices.length; j++) {
                            collectedVertices.push(vertices[j]);
                        }
                    }
                }
            }
        }

        if (collectedVertices.length === 0) return null;

        return {
            edgeVertexPairs: collectedVertices,
            edgeSayisi: collectedVertices.length / 2,
            mod: TopolojikSecimModu.FACE_LOOP,
            faceId: faces.faceA >= 0 ? faces.faceA : faces.faceB,
        };
    }

    /**
     * Edge Chain Secimi: Baslangic edge'inden baslayarak, ortak face paylasan
     * bitisik edge'leri zincirleme sec.
     *
     * Algoritma: BFS - secilen edge'in face komsularindan, ayni face'i paylasan diger
     * edge'leri bul. Tangent sureklilik kontrolu yapar (opsiyonel).
     *
     * @param edgeGeometry Edge BufferGeometry
     * @param hitVertexIndex Baslangic edge'inin vertex index'i
     * @param maxDepth Maksimum zincir derinligi (varsayilan: 100)
     */
    selectEdgeChain(
        edgeGeometry: THREE.BufferGeometry,
        hitVertexIndex: number,
        maxDepth: number = 100
    ): TopolojikSecimSonuc | null {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return null;

        const startEdgeId = index.vertexToEdgeId.get(hitVertexIndex);
        if (startEdgeId === undefined || startEdgeId < 0) return null;

        const posAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute;
        const curveTypeAttr = edgeGeometry.getAttribute('curveType') as THREE.BufferAttribute | undefined;
        const radiusAttr = edgeGeometry.getAttribute('radius') as THREE.BufferAttribute | undefined;

        // Baslangic edge bilgileri
        const startVertices = index.edgeIdToVertices.get(startEdgeId);
        if (!startVertices || startVertices.length < 2) return null;

        const startCurveType = curveTypeAttr ? curveTypeAttr.getX(startVertices[0]) : 0;
        const startRadius = radiusAttr ? radiusAttr.getX(startVertices[0]) : 0;

        // BFS ile zincirleme secim
        const visited = new Set<number>();
        visited.add(startEdgeId);
        const queue: number[] = [startEdgeId];
        const collectedVertices: number[] = [];

        // Baslangic edge'ini ekle
        for (let i = 0; i < startVertices.length; i++) {
            collectedVertices.push(startVertices[i]);
        }

        let depth = 0;
        while (queue.length > 0 && depth < maxDepth) {
            const currentEdgeId = queue.shift()!;
            depth++;

            const currentFaces = index.edgeToFaces.get(currentEdgeId);
            if (!currentFaces) continue;

            // Her iki komsufaceyi tara
            for (const fId of [currentFaces.faceA, currentFaces.faceB]) {
                if (fId < 0) continue;

                const faceEdges = index.faceToEdges.get(fId);
                if (!faceEdges) continue;

                for (let i = 0; i < faceEdges.length; i += 2) {
                    const neighborEdgeId = index.vertexToEdgeId.get(faceEdges[i]);
                    if (neighborEdgeId === undefined || neighborEdgeId < 0) continue;
                    if (visited.has(neighborEdgeId)) continue;

                    // Geometrik bitisiklik kontrolu: Edge'ler ortak vertex paylasiyorlar mi?
                    if (!this._edgesShareVertex(index, currentEdgeId, neighborEdgeId, posAttr)) {
                        continue;
                    }

                    // Tangent sureklilik kontrolu: Ayni curve type ve yakin radius
                    const neighborVertices = index.edgeIdToVertices.get(neighborEdgeId);
                    if (!neighborVertices || neighborVertices.length < 2) continue;

                    const neighborCurveType = curveTypeAttr ? curveTypeAttr.getX(neighborVertices[0]) : 0;
                    const neighborRadius = radiusAttr ? radiusAttr.getX(neighborVertices[0]) : 0;

                    // Curve type uyumluluk: Ayni tip olmali (line-line veya circle-circle)
                    if (startCurveType > 0 && neighborCurveType > 0 && startCurveType !== neighborCurveType) {
                        continue;
                    }

                    // Radius uyumluluk: Radius farki %5'ten az olmali
                    if (startRadius > 0 && neighborRadius > 0) {
                        const radiusDiff = Math.abs(startRadius - neighborRadius) / Math.max(startRadius, neighborRadius);
                        if (radiusDiff > 0.05) continue;
                    }

                    visited.add(neighborEdgeId);
                    queue.push(neighborEdgeId);

                    for (let j = 0; j < neighborVertices.length; j++) {
                        collectedVertices.push(neighborVertices[j]);
                    }
                }
            }
        }

        if (collectedVertices.length <= 2) return null; // Sadece baslangic edge'i varsa, chain yok

        return {
            edgeVertexPairs: collectedVertices,
            edgeSayisi: collectedVertices.length / 2,
            mod: TopolojikSecimModu.EDGE_CHAIN,
        };
    }

    /**
     * Radius Group Secimi: Ayni radius grubundaki tum kenarlari sec
     * @param edgeGeometry Edge BufferGeometry
     * @param hitVertexIndex Secilen edge'in vertex index'i
     */
    selectRadiusGroup(edgeGeometry: THREE.BufferGeometry, hitVertexIndex: number): TopolojikSecimSonuc | null {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return null;

        const radiusGroupAttr = edgeGeometry.getAttribute('radiusGroup') as THREE.BufferAttribute | undefined;
        const radiusAttr = edgeGeometry.getAttribute('radius') as THREE.BufferAttribute | undefined;

        if (!radiusGroupAttr) return null;

        const targetGroup = radiusGroupAttr.getX(hitVertexIndex);
        if (targetGroup < 0) return null;

        const edgeVertices = index.radiusGroupToEdges.get(targetGroup);
        if (!edgeVertices || edgeVertices.length === 0) return null;

        const radius = radiusAttr ? radiusAttr.getX(hitVertexIndex) : undefined;

        return {
            edgeVertexPairs: edgeVertices,
            edgeSayisi: edgeVertices.length / 2,
            mod: TopolojikSecimModu.RADIUS_GROUP,
            radiusGroupId: targetGroup,
            radius,
        };
    }

    /**
     * Aktif moda gore secim yap
     * @param edgeGeometry Edge BufferGeometry
     * @param hitVertexIndex Secilen edge'in vertex index'i (edgeIndex * 2)
     * @param shiftKey Shift tusu basilimi
     */
    selectByMode(
        edgeGeometry: THREE.BufferGeometry,
        hitVertexIndex: number,
        shiftKey: boolean = false,
        dblClick: boolean = false
    ): TopolojikSecimSonuc | null {
        // Double-click: Face loop
        if (dblClick) {
            return this.selectFaceLoopFromEdge(edgeGeometry, hitVertexIndex);
        }

        // Shift+Click: Edge chain
        if (shiftKey && this.secimModu !== TopolojikSecimModu.RADIUS_GROUP) {
            return this.selectEdgeChain(edgeGeometry, hitVertexIndex);
        }

        // Mod'a gore sec
        switch (this.secimModu) {
            case TopolojikSecimModu.FACE_LOOP:
                return this.selectFaceLoopFromEdge(edgeGeometry, hitVertexIndex);
            case TopolojikSecimModu.EDGE_CHAIN:
                return this.selectEdgeChain(edgeGeometry, hitVertexIndex);
            case TopolojikSecimModu.RADIUS_GROUP:
                return this.selectRadiusGroup(edgeGeometry, hitVertexIndex);
            case TopolojikSecimModu.TEK_KENAR:
            default:
                return null; // Normal tek kenar secimi, ozel islem yok
        }
    }

    // ============================================================
    // HIGHLIGHT GEOMETRY BUILDER
    // ============================================================

    /**
     * Topolojik secim sonucundan highlight geometry olustur
     * @param edgeGeometry Kaynak edge geometry
     * @param sonuc Topolojik secim sonucu
     * @param worldMatrix World transform matrix
     * @returns Highlight icin Float32Array (positions) veya null
     */
    buildHighlightPositions(
        edgeGeometry: THREE.BufferGeometry,
        sonuc: TopolojikSecimSonuc
    ): Float32Array | null {
        const posAttr = edgeGeometry.getAttribute('position') as THREE.BufferAttribute;
        if (!posAttr) return null;

        const pairs = sonuc.edgeVertexPairs;
        const positions = new Float32Array(pairs.length * 3);

        let offset = 0;
        for (let i = 0; i < pairs.length; i++) {
            const vi = pairs[i];
            if (vi < 0 || vi >= posAttr.count) continue;
            positions[offset++] = posAttr.getX(vi);
            positions[offset++] = posAttr.getY(vi);
            positions[offset++] = posAttr.getZ(vi);
        }

        // Trim if needed
        if (offset < positions.length) {
            return positions.subarray(0, offset);
        }

        return positions;
    }

    // ============================================================
    // UTILITY & PRIVATE
    // ============================================================

    /**
     * Iki edge'in ortak bir vertex paylasiyor mu kontrol et
     * Geometrik yakinlik: 0.01mm tolerans
     */
    private _edgesShareVertex(
        index: TopologyIndex,
        edgeA: number,
        edgeB: number,
        posAttr: THREE.BufferAttribute
    ): boolean {
        const verticesA = index.edgeIdToVertices.get(edgeA);
        const verticesB = index.edgeIdToVertices.get(edgeB);
        if (!verticesA || !verticesB) return false;

        const tol = 0.01; // mm
        const tolSq = tol * tol;

        // Edge A'nin baslangic ve bitis vertex'leri
        // (tessellated edge'lerde ilk ve son vertex'leri al)
        const aStart = verticesA[0];
        const aEnd = verticesA[verticesA.length - 1];

        // Edge B'nin baslangic ve bitis vertex'leri
        const bStart = verticesB[0];
        const bEnd = verticesB[verticesB.length - 1];

        const endpoints = [aStart, aEnd];
        const bEndpoints = [bStart, bEnd];

        for (const va of endpoints) {
            const ax = posAttr.getX(va), ay = posAttr.getY(va), az = posAttr.getZ(va);
            for (const vb of bEndpoints) {
                const dx = ax - posAttr.getX(vb);
                const dy = ay - posAttr.getY(vb);
                const dz = az - posAttr.getZ(vb);
                if (dx * dx + dy * dy + dz * dz < tolSq) {
                    return true;
                }
            }
        }
        return false;
    }

    /** Cache'den eski entry'leri temizle */
    private _evictOldCache(): void {
        if (this._indexCache.size < this.MAX_CACHE_SIZE) return;

        const now = Date.now();
        const toRemove: string[] = [];

        this._cacheTimestamps.forEach((ts, uuid) => {
            if (now - ts > this.CACHE_TTL) {
                toRemove.push(uuid);
            }
        });

        // TTL ile temizleme yetmezse, en eski yarisi kaldir
        if (toRemove.length === 0 && this._indexCache.size >= this.MAX_CACHE_SIZE) {
            const entries = Array.from(this._cacheTimestamps.entries())
                .sort((a, b) => a[1] - b[1]);
            const removeCount = Math.floor(entries.length / 2);
            for (let i = 0; i < removeCount; i++) {
                toRemove.push(entries[i][0]);
            }
        }

        for (const uuid of toRemove) {
            this._indexCache.delete(uuid);
            this._cacheTimestamps.delete(uuid);
        }
    }

    /** Tum cache'i temizle */
    clearCache(): void {
        this._indexCache.clear();
        this._cacheTimestamps.clear();
    }

    /** Istatistikler */
    getStats(): { cacheSize: number; modu: TopolojikSecimModu } {
        return {
            cacheSize: this._indexCache.size,
            modu: this.secimModu,
        };
    }

    /** Belirli bir face'e ait edge sayisini dondur (onizleme icin) */
    getFaceEdgeCount(edgeGeometry: THREE.BufferGeometry, faceId: number): number {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return 0;
        const edges = index.faceToEdges.get(faceId);
        return edges ? edges.length / 2 : 0;
    }

    /** Belirli bir radius grubundaki edge sayisini dondur */
    getRadiusGroupEdgeCount(edgeGeometry: THREE.BufferGeometry, radiusGroupId: number): number {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return 0;
        const edges = index.radiusGroupToEdges.get(radiusGroupId);
        return edges ? edges.length / 2 : 0;
    }

    /** Geometry'deki tum face ID'lerini dondur */
    getAllFaceIds(edgeGeometry: THREE.BufferGeometry): number[] {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return [];
        return Array.from(index.faceToEdges.keys());
    }

    /** Geometry'deki tum radius group ID'lerini dondur */
    getAllRadiusGroupIds(edgeGeometry: THREE.BufferGeometry): number[] {
        const index = this.getOrBuildIndex(edgeGeometry);
        if (!index) return [];
        return Array.from(index.radiusGroupToEdges.keys());
    }
}

/** Singleton accessor */
export function getTopologicalSelection(): FikoTopologicalSelection {
    return FikoTopologicalSelection.getInstance();
}
